import React from 'react';
import { useTranslation } from 'react-i18next';
import { WizardState } from '../wizardTypes';
import { getRevenueSourceDisplayName } from '../../../types/RevenueSource';
import { getRevenueSourceTypeDisplayName } from '../../../types/RevenueSourceType';
import { SupplierLogo } from '../../ui/SupplierLogo';

interface Props {
  state: WizardState;
  onSave: () => void;
  isSaving: boolean;
  error: string | null;
}

const ConfirmationStep: React.FC<Props> = ({ state, onSave, isSaving, error }) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text dark:text-text-dark">{t('wizard.confirmation.title')}</h2>
        <p className="text-muted dark:text-muted-dark mt-2">{t('wizard.confirmation.subtitle')}</p>
      </div>

      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700 p-6 space-y-4">
        {state.type === 'supplier' && state.platform && (
          <>
            <div className="flex items-center gap-3 pb-4 border-b border-zinc-200 dark:border-zinc-700">
              <SupplierLogo platform={state.platform} alt={state.platform} size="large" />
              <div>
                <p className="font-semibold text-text dark:text-text-dark capitalize">{state.platform.replace(/_/g, ' ')}</p>
                <p className="text-sm text-muted dark:text-muted-dark">{t('wizard.confirmation.supplierLabel')}</p>
              </div>
            </div>
            <Row label={t('wizard.confirmation.section')} value={state.sectionName || '-'} />
            <Row label={t('wizard.confirmation.clientName')} value={state.orderClientNames.length > 0 ? state.orderClientNames.join(', ') : '-'} />
            <Row
              label={t('wizard.confirmation.emails')}
              value={state.orderEmails.length > 0 ? state.orderEmails.join(', ') : '-'}
            />
          </>
        )}

        {state.type === 'revenue' && state.revenueSourceName && (
          <>
            <div className="pb-4 border-b border-zinc-200 dark:border-zinc-700">
              <p className="font-semibold text-text dark:text-text-dark">
                {getRevenueSourceDisplayName(state.revenueSourceName)}
              </p>
              <p className="text-sm text-muted dark:text-muted-dark">{t('wizard.confirmation.revenueLabel')}</p>
            </div>
            <Row
              label={t('wizard.confirmation.sourceType')}
              value={state.revenueSourceType ? getRevenueSourceTypeDisplayName(state.revenueSourceType) : '-'}
            />
            <Row label={t('wizard.confirmation.restaurantName')} value={state.restaurantNameInPOS || '-'} />
            <Row
              label={t('wizard.confirmation.sections')}
              value={state.relevantSections.length > 0 ? state.relevantSections.join(', ') : '-'}
            />
          </>
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3 text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={isSaving}
          className="px-6 py-3 rounded-xl bg-emerald-500 dark:bg-emerald-600 text-white dark:text-white hover:bg-emerald-600 dark:hover:bg-emerald-700 transition font-medium disabled:opacity-50"
        >
          {isSaving ? t('wizard.confirmation.saving') : t('wizard.confirmation.saveAndFinish')}
        </button>
      </div>
    </div>
  );
};

const Row: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex justify-between items-start">
    <span className="text-sm text-muted dark:text-muted-dark">{label}</span>
    <span className="text-sm font-medium text-text dark:text-text-dark text-right max-w-[60%]">{value}</span>
  </div>
);

export default ConfirmationStep;
